import { pbRepository } from "./pocketbase-repository";
import { auth } from "./auth.service";
import type { Subject } from "@/types/models";

const repo = pbRepository<Subject>("subjects");

/**
 * Hinweis: Die Eigentümer-Filterung (nur eigene Datensätze) wird über die
 * PocketBase-API-Rules erzwungen (owner = @request.auth.id). Die Services
 * setzen `owner` beim Anlegen und müssen ihn beim Lesen nicht filtern.
 */
/** "Sonstiges" ist der Sammeltopf für Orga-Kram ohne echtes Fach – kein Unterrichtsfach. */
export function isSonstigesSubject(s: Subject): boolean {
  return s.title.trim().toLowerCase() === "sonstiges";
}

/** "Sonstiges" gehört überall ganz nach unten, egal welche Position gesetzt ist. */
function sonstigesLast(subjects: Subject[]): Subject[] {
  return [...subjects.filter((s) => !isSonstigesSubject(s)), ...subjects.filter(isSonstigesSubject)];
}

// Verhindert doppeltes Anlegen, wenn mehrere Seiten (Home/Hausaufgaben/...)
// beim Start gleichzeitig `ensureSonstiges()` aufrufen, bevor das erste
// Anlegen fertig ist.
let ensureSonstigesPromise: Promise<void> | null = null;

export const subjectsService = {
  list(): Promise<Subject[]> {
    return repo.list({ sort: "position,created" }).then(sonstigesLast);
  },

  /**
   * Wie `list()`, aber ohne "Sonstiges" – für Stellen, die echte
   * Unterrichtsfächer meinen (Startseite, Prüfungen, Stundenplan-Zuweisung).
   * Nur die Hausaufgaben-Fachauswahl nutzt weiter `list()`.
   */
  listReal(): Promise<Subject[]> {
    return subjectsService.list().then((s) => s.filter((x) => !isSonstigesSubject(x)));
  },

  /** Legt "Sonstiges" (Orga-Hausaufgaben ohne echtes Fach) an, falls es noch nicht existiert. */
  ensureSonstiges(): Promise<void> {
    ensureSonstigesPromise ??= (async () => {
      const subjects = await repo.list({ sort: "created" });
      if (subjects.some((s) => s.title.trim().toLowerCase() === "sonstiges")) return;
      await subjectsService.create({ title: "Sonstiges", color: "#6b7280", icon: "clock" });
    })().finally(() => {
      ensureSonstigesPromise = null;
    });
    return ensureSonstigesPromise;
  },

  get(id: string): Promise<Subject> {
    return repo.getOne(id);
  },

  create(data: {
    title: string;
    color: string;
    icon?: string;
    category?: string;
    position?: number;
  }): Promise<Subject> {
    return repo.create({
      ...data,
      owner: auth.currentUser()?.id,
    } as Partial<Subject>);
  },

  update(id: string, data: Partial<Subject>): Promise<Subject> {
    return repo.update(id, data);
  },

  remove(id: string): Promise<void> {
    return repo.remove(id);
  },
};
